import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Shield, LayoutDashboard, Store, ChevronDown, ChevronLeft, Check, Search, MapPin, Sparkles } from "lucide-react";
import { useActiveContext, ROLES } from "@/context/ActiveContext";

const ROLE_ICON = {
  admin: Shield, council: Shield, manager: Building2,
  frontdesk: Store, enhancer: Sparkles, member: LayoutDashboard,
};

export default function ContextSwitcher({ mobile = false }) {
  const ctx = useActiveContext();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("root"); // "root" | "center"
  const [pendingRole, setPendingRole] = useState(null);
  const [q, setQ] = useState("");

  const allCenters = ctx?.centers || [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return allCenters;
    return allCenters.filter((c) =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.city || "").toLowerCase().includes(term) ||
      (c.state || "").toLowerCase().includes(term));
  }, [q, allCenters]);

  if (!ctx || !ctx.user) return null;
  const { roleKeys, centers, canScope, activeRole, activeCenterId, activeCenterName, setContext } = ctx;

  // Nothing to switch between → hide entirely (e.g. plain members, pure enhancers).
  const showable = roleKeys.length > 1 || (canScope && centers.length > 1);
  if (!showable) return null;

  const ActiveIcon = ROLE_ICON[activeRole] || LayoutDashboard;
  const activeMeta = ROLES[activeRole] || {};
  const pillLabel = activeMeta.scoped && activeCenterName
    ? `${activeMeta.short} · ${activeCenterName}`
    : (activeMeta.label || "Workspace");

  const close = () => { setOpen(false); setStep("root"); setPendingRole(null); setQ(""); };

  const activate = (roleKey, centerId) => {
    setContext(roleKey, centerId);
    close();
    qc.invalidateQueries();
    nav(ROLES[roleKey].route);
  };

  const onRoleClick = (roleKey) => {
    const meta = ROLES[roleKey];
    if (meta.scoped) {
      // One center only → activate straight away; multiple → pick a center.
      const pickable = centers;
      if (pickable.length <= 1 && !meta.allowAll) {
        activate(roleKey, pickable[0]?.id || "");
        return;
      }
      if (pickable.length <= 1 && meta.allowAll && pickable.length === 1) {
        activate(roleKey, pickable[0].id);
        return;
      }
      setPendingRole(roleKey);
      setStep("center");
    } else {
      activate(roleKey, activeCenterId);
    }
  };

  const pendingMeta = pendingRole ? ROLES[pendingRole] : null;

  const Panel = (
    <div className={mobile ? "mt-2" : "absolute right-0 top-full pt-2 w-72 z-[60]"} data-testid="context-switcher-menu">
      <div className="bg-white rounded-2xl shadow-xl border border-[#E5E0D8] p-2">
        {step === "root" && (
          <>
            <p className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-[#B5B0A6]">Active context</p>
            {roleKeys.map((rk) => {
              const meta = ROLES[rk];
              const Icon = ROLE_ICON[rk] || LayoutDashboard;
              const isActive = rk === activeRole;
              return (
                <button key={rk} onClick={() => onRoleClick(rk)} data-testid={`ctx-role-${rk}`}
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-[#144F75]/[0.07] text-[#144F75] font-semibold" : "text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"}`}>
                  <Icon size={16} className="shrink-0" />
                  <span className="flex-1 min-w-0">
                    {meta.label}
                    {isActive && meta.scoped && activeCenterName && (
                      <span className="block text-[11px] font-normal text-[#8FA89B] truncate">{activeCenterName}</span>
                    )}
                  </span>
                  {meta.scoped
                    ? <ChevronDown size={15} className="-rotate-90 text-[#B5B0A6] shrink-0" />
                    : (isActive && <Check size={15} className="text-[#144F75] shrink-0" />)}
                </button>
              );
            })}
          </>
        )}

        {step === "center" && pendingMeta && (
          <div data-testid="ctx-center-step">
            <button onClick={() => { setStep("root"); setPendingRole(null); setQ(""); }} data-testid="ctx-center-back"
              className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-[#4A5D6B] hover:text-[#144F75]">
              <ChevronLeft size={14} /> Back
            </button>
            <p className="px-3 pt-0.5 pb-0.5 font-serif text-base text-[#1A242B]">{pendingMeta.label}</p>
            <p className="px-3 pb-2 text-[11px] text-[#8FA89B]">Choose which center to operate</p>
            {centers.length > 6 && (
              <div className="px-2 pb-2">
                <div className="flex items-center gap-2 rounded-xl border border-[#E5E0D8] px-2.5 py-1.5">
                  <Search size={14} className="text-[#B5B0A6]" />
                  <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search centers…"
                    data-testid="ctx-center-search" className="w-full text-sm outline-none bg-transparent" />
                </div>
              </div>
            )}
            <div className="max-h-64 overflow-y-auto">
              {pendingMeta.allowAll && !q && (
                <button onClick={() => activate(pendingRole, "__all__")} data-testid="ctx-center-all"
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${activeRole === pendingRole && activeCenterId === "__all__" ? "bg-[#144F75]/[0.07] text-[#144F75] font-semibold" : "text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"}`}>
                  <Building2 size={16} className="shrink-0" />
                  <span className="flex-1">All Centers <span className="text-[10px] uppercase tracking-wide text-[#B5B0A6]">overview</span></span>
                  {activeRole === pendingRole && activeCenterId === "__all__" && <Check size={15} className="text-[#144F75]" />}
                </button>
              )}
              {filtered.map((c) => {
                const isActive = activeRole === pendingRole && activeCenterId === c.id;
                return (
                  <button key={c.id} onClick={() => activate(pendingRole, c.id)} data-testid={`ctx-center-${c.id}`}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-[#144F75]/[0.07] text-[#144F75] font-semibold" : "text-[#4A5D6B] hover:bg-[#144F75]/[0.05]"}`}>
                    <MapPin size={15} className="shrink-0 text-[#D98E73]" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{c.name}</span>
                      {(c.city || c.state) && <span className="block text-[11px] text-[#8FA89B] truncate">{[c.city, c.state].filter(Boolean).join(", ")}</span>}
                    </span>
                    {isActive && <Check size={15} className="text-[#144F75] shrink-0" />}
                  </button>
                );
              })}
              {filtered.length === 0 && <p className="px-3 py-4 text-sm text-[#8FA89B] text-center">No centers found.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (mobile) {
    return (
      <div data-testid="context-switcher-mobile" className="py-2 border-t border-[#E5E0D8]/60 mt-2">
        <button onClick={() => { setOpen((v) => !v); setStep("root"); }} data-testid="context-switcher-btn-mobile"
          className="w-full flex items-center gap-2 text-left text-[#144F75] py-1">
          <ActiveIcon size={16} className="shrink-0" />
          <span className="flex-1 text-sm font-medium truncate">{pillLabel}</span>
          <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && Panel}
      </div>
    );
  }

  return (
    <div className="relative">
      <button onClick={() => { setOpen((v) => !v); setStep("root"); }} data-testid="context-switcher-btn"
        className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white px-3 py-2 text-xs 2xl:text-sm max-w-[150px] 2xl:max-w-[170px] transition-colors">
        <ActiveIcon size={15} className="shrink-0 text-[#F0C9B8]" />
        <span className="truncate">{pillLabel}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-[55] cursor-default" onClick={close} aria-label="Close" tabIndex={-1} data-testid="context-switcher-backdrop" />
          {Panel}
        </>
      )}
    </div>
  );
}
